import React, { useMemo, useState } from 'react'
import styled from 'styled-components'
import { useSelector } from 'react-redux'
import { IEntriesItem } from '../../../../../modules/API/Miun/models/sortingList'
import { IWarhouseListItem } from '../../../../../modules/API/Defaults/models/warhouseList'
import { IPackagesItem } from '../../../../../modules/API/Defaults/models/packages'
import { RootState } from '../../../../../../store/reducers/rootReducer'
import selectBrodMigdal from '../../../../../generic/utils/selectBrodMigdal'
import IconTextField from '../../../../../generic/ui/Input/IconTextField'
import HashIcon from '../../../../../generic/icons/IconLib/HashIcon'
import { AccentButton } from '../../../../../generic/ui/Button/AccentButton'
import WarehouseSelector from '../../../../../generic/ui/Selectors/WarehouseSelector'
import PackageSelector from '../../../../../generic/ui/Selectors/PackageSelector'
import {
    FormShell,
    FormGrid,
    FormField,
    FormFooter,
} from '../../../../../generic/ui/FormLayout'
import DivideItemCard from './DivideItemCard'

type Props = {
    item: IEntriesItem
    saveFunc: (
        item: IEntriesItem,
        qty: number,
        restItem: IEntriesItem,
        tranzit: number,
        packID: number,
    ) => void
    warehouseList?: IWarhouseListItem[]
    packageList?: IPackagesItem[]
}

const DivideModal: React.FC<Props> = ({ item, saveFunc, warehouseList, packageList }) => {
    const [qty, setQty] = useState<number>(0)
    const [selectedPackage, setSelectedPackage] = useState<number>(0)
    const [selectedWarehouse, setSelectedWarehouse] = useState<number>(0)

    const defaults = useSelector((state: RootState) => state.defaults.defaults)
    const isAllowToChangeTranzit = !!selectBrodMigdal(defaults, 405, 'Logical_Value')

    const render = useMemo(() => {
        const grossPerUnit = item.AmountPack > 0 ? item.Gross / item.AmountPack : 0
        const restGross = Math.round(item.Gross - grossPerUnit * qty)
        const restPacks = item.AmountPack - qty
        return {
            rest: {
                ...item,
                Gross: restGross,
                AmountPack: restPacks,
                isSelected: false,
            },
            current: {
                ...item,
                Gross: item.Gross - restGross,
                AmountPack: item.AmountPack - restPacks,
                isSelected: false,
            },
        }
    }, [qty, item])

    const isAlowtoSave = qty > 0 && item.AmountPack - qty > 0

    const warehouseObj = useMemo(
        () => warehouseList?.find((w) => w.WarehouseID === selectedWarehouse) ?? null,
        [warehouseList, selectedWarehouse],
    )
    const packageObj = useMemo(
        () => packageList?.find((p) => p.PackID === selectedPackage) ?? null,
        [packageList, selectedPackage],
    )

    const handleSave = () => {
        if (!isAlowtoSave) return
        saveFunc(item, qty, render.rest, selectedWarehouse, selectedPackage)
    }

    return (
        <FormShell>
            <FormGrid>
                <FormField label='כמות לפיצול'>
                    <IconTextField
                        type='number'
                        leadingIcon={<HashIcon />}
                        value={qty || ''}
                        onChange={(e) => setQty(Number(e.target.value))}
                        onFocus={(e) => e.currentTarget.select()}
                        placeholder='—'
                    />
                </FormField>
                {qty > 0 && packageList && (
                    <FormField label='סוג אריזה יציאה'>
                        <PackageSelector
                            packages={packageList}
                            value={packageObj}
                            onSelect={(p) => setSelectedPackage(p?.PackID ?? 0)}
                        />
                    </FormField>
                )}
                {qty > 0 && warehouseList && isAllowToChangeTranzit && (
                    <FormField label='מחסן יציאה'>
                        <WarehouseSelector
                            warehouses={warehouseList}
                            value={warehouseObj}
                            onSelect={(w) => setSelectedWarehouse(w?.WarehouseID ?? 0)}
                        />
                    </FormField>
                )}
            </FormGrid>

            <Cards>
                <CardGroup>
                    <CardLabel>יתרה</CardLabel>
                    <DivideItemCard item={render.rest} />
                </CardGroup>
                {qty > 0 && (
                    <CardGroup>
                        <CardLabel>מפוצל</CardLabel>
                        <DivideItemCard item={render.current} />
                    </CardGroup>
                )}
            </Cards>

            <FormFooter>
                <AccentButton
                    type='button'
                    onClick={handleSave}
                    disabled={!isAlowtoSave}
                    fullWidth={false}
                >
                    שמור
                </AccentButton>
            </FormFooter>
        </FormShell>
    )
}

export default DivideModal

const Cards = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.875rem;
`

const CardGroup = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.375rem;
`

const CardLabel = styled.span`
    font-family: ${({ theme }) => theme.fonts.sans};
    font-size: 0.75rem;
    font-weight: 600;
    color: ${({ theme }) => theme.colors.ink2};
    text-align: start;
    padding-inline-start: 0.25rem;
`

